import { useEffect, useState, type ReactNode } from "react";
import { BookOpenCheck } from "lucide-react";

import { getPublicContentByPath } from "@/lib/api";
import { displayDate, titleCase } from "@/lib/utils";

type Entry = NonNullable<Awaited<ReturnType<typeof getPublicContentByPath>>> & {
  author?: { display_name: string } | null;
  reviewer?: { display_name: string } | null;
  cta?: { href?: string; label?: string } | null;
};

export function CmsPublicEntry({
  path,
  fallback,
}: {
  path: string;
  fallback: ReactNode;
}) {
  const [entry, setEntry] = useState<Entry>();

  useEffect(() => {
    let live = true;
    getPublicContentByPath(path)
      .then((value) => live && value && setEntry(value as Entry))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [path]);

  if (!entry) return fallback;
  const blocks = Array.isArray(entry.body) ? entry.body : [];
  return (
    <article className="bg-surface-paper px-5 py-20 text-on-paper md:px-8 lg:py-28">
      <div className="mx-auto max-w-4xl">
        <BookOpenCheck className="size-10 text-teal-deep" />
        <p className="marketing-eyebrow-light mt-6">
          {titleCase(entry.content_type)}
          {entry.category ? ` · ${entry.category}` : ""}
        </p>
        <h1 className="mt-5 font-serif text-5xl leading-[1.03] tracking-[-.045em] sm:text-6xl">
          {entry.title}
        </h1>
        <p className="mt-7 text-xl leading-8 text-on-paper-muted">
          {entry.excerpt}
        </p>
        <div className="mt-10 flex flex-wrap gap-x-5 gap-y-2 border-y border-line-light py-4 text-xs text-on-paper-muted">
          <span>Published {displayDate(entry.published_at || entry.updated_at)}</span>
          {entry.author?.display_name && <span>By {entry.author.display_name}</span>}
          {entry.reviewer?.display_name && (
            <span>Reviewed by {entry.reviewer.display_name}</span>
          )}
        </div>
        {entry.hero_image_url && (
          <img
            alt=""
            className="mt-10 w-full rounded-[2rem] border border-line-light"
            src={entry.hero_image_url}
          />
        )}
        <div className="mt-10 grid gap-6 text-base leading-8 text-on-paper-muted">
          {blocks.map((block, index) => {
            if (block.type === "heading")
              return (
                <h2 className="mt-4 font-serif text-3xl text-on-paper" key={index}>
                  {block.text}
                </h2>
              );
            if (block.type === "bullet")
              return (
                <div className="flex gap-3" key={index}>
                  <span aria-hidden="true" className="mt-3 size-1.5 shrink-0 rounded-full bg-teal-deep" />
                  <p>{block.text}</p>
                </div>
              );
            if (block.type === "quote")
              return (
                <blockquote className="border-l-4 border-teal-deep pl-5 font-serif text-2xl leading-9 text-on-paper" key={index}>
                  {block.text}
                </blockquote>
              );
            if (block.type === "callout")
              return (
                <aside className="rounded-2xl border border-line-light bg-white p-5 text-on-paper" key={index}>
                  {block.text}
                </aside>
              );
            return <p key={index}>{block.text}</p>;
          })}
        </div>
        {entry.cta?.href && entry.cta?.label && (
          <a
            className="mt-12 inline-flex min-h-11 items-center rounded-full bg-teal-deep px-6 py-3 font-semibold text-white"
            href={String(entry.cta.href)}
          >
            {String(entry.cta.label)}
          </a>
        )}
      </div>
    </article>
  );
}
